<script lang="ts">
	import type { PageData } from './$types';
	import { ShoppingBasket } from 'lucide-svelte';
	import type { Component } from 'svelte';

	let { data } = $props<{ data: PageData }>();

	// Lazy load the map component only when needed
	let MarketsMapComponent = $state<Component | null>(null);
	let loadingMap = $state(false);
	let showMap = $state(false);

	async function loadMap() {
		if (MarketsMapComponent || loadingMap) return;
		loadingMap = true;
		try {
			const module = await import('$lib/components/maps/MarketsMap.svelte');
			MarketsMapComponent = module.default as Component;
			showMap = true;
		} catch (error) {
			console.error('Failed to load map:', error);
		} finally {
			loadingMap = false;
		}
	}

	// Get some stats for the hero section
	const activeMarketsCount = $derived(
		data.markets?.filter(
			(m: { activeProductsCount: number; market: { disabled: boolean } }) =>
				m.activeProductsCount > 0 && !m.market.disabled
		).length || 0
	);
	const totalGrowers = $derived(
		data.markets?.reduce(
			(sum: number, m: { activeGrowersCount?: number }) => sum + (m.activeGrowersCount || 0),
			0
		) || 0
	);
</script>

<!-- Hero Section -->
<section class="hero">
	<div class="container">
		<h1>Sell Direct. Shop Local. Build Community.</h1>
		<p>
			The simple way for farmers to sell online and neighbors to buy fresh. Your whole market,
			organized in one place—no tech skills needed.
		</p>
		<div class="hero-buttons">
			<a href="/markets" class="btn btn-primary">Shop Local Markets</a>
			<a href="/markets/new" class="btn btn-secondary">Start Your Market (It's Free)</a>
		</div>
	</div>
</section>

<!-- Stats Section -->
<section class="stats-section">
	<div class="container">
		<div class="stats-grid">
			<div class="stat-item">
				<div class="stat-number">{activeMarketsCount}</div>
				<div class="stat-description">Active Markets</div>
			</div>
			<div class="stat-item">
				<div class="stat-number">{totalGrowers}+</div>
				<div class="stat-description">Local Growers</div>
			</div>
			<div class="stat-item">
				<div class="stat-number">20+</div>
				<div class="stat-description">Years of Service</div>
			</div>
		</div>
	</div>
</section>

<!-- Features Section -->
<section class="features">
	<div class="container">
		<h2 class="mb-4 text-center">Everything You Need, Nothing You Don't</h2>
		<div class="features-grid">
			<div class="feature-card">
				<div class="feature-icon">🌱</div>
				<h3>Simple for Farmers</h3>
				<p>
					Add products with your phone. See what's ordered. Get paid automatically. That's it—back
					to farming.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">
					<ShoppingBasket size={48} />
				</div>
				<h3>Convenient for Customers</h3>
				<p>
					Shop Tuesday night. Pick up Saturday morning. Know exactly what's available before you
					drive to the market.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">📊</div>
				<h3>Organized for Managers</h3>
				<p>
					See all orders in one place. Print labels. Track payments. Run pickup day like clockwork.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">💳</div>
				<h3>Flexible Payments</h3>
				<p>
					Accept cards online or cash at pickup. Farmers get paid directly—we never hold your money.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">📱</div>
				<h3>Works on Any Device</h3>
				<p>Phone, tablet, or computer—it just works. No app to download, nothing to install.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">🔧</div>
				<h3>Your Market, Your Brand</h3>
				<p>Add your logo and colors. It'll look like your market, not some generic website.</p>
			</div>
			<div class="feature-card showcase-feature">
				<div class="feature-icon">📱</div>
				<h3>Built with Mobile-First Design</h3>
				<p>
					Every interaction is crafted for touch, optimized for speed, and accessible to all.
					Explore our comprehensive component library and design system that powers beautiful market
					experiences.
				</p>
				<a href="/showcase" class="feature-link">View Design Showcase →</a>
			</div>
		</div>
	</div>
</section>

<!-- Map Section -->
<section class="markets-section">
	<div class="container">
		<h2 class="mb-4 text-center">Find a Market Near You</h2>
		{#if data.mapMarkers}
			<div class="map-container">
				{#if showMap && MarketsMapComponent}
					{@const MapComponent = MarketsMapComponent}
					<MapComponent markers={data.mapMarkers} />
				{:else}
					<div
						class="map-placeholder"
						onclick={loadMap}
						onkeydown={(e) => e.key === 'Enter' && loadMap()}
						role="button"
						tabindex="0"
					>
						<div class="placeholder-content">
							<div class="placeholder-icon">🗺️</div>
							<h3>Interactive Market Map</h3>
							<p>Click to load an interactive map showing all markets</p>
							{#if loadingMap}
								<div class="loading-spinner"></div>
							{:else}
								<button class="load-map-btn" type="button">Load Map</button>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		{/if}
		<div class="mt-4 text-center">
			<a href="/markets" class="btn btn-primary">Browse All Markets</a>
		</div>
	</div>
</section>

<!-- How It Works -->
<section class="features">
	<div class="container">
		<h2 class="mb-4 text-center">How It Works</h2>
		<div class="features-grid">
			<div class="feature-card">
				<div class="feature-icon">1</div>
				<h3>Farmers List Products</h3>
				<p>Monday morning: snap a photo, set a price, done. Takes about 30 seconds per product.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">2</div>
				<h3>Customers Order Online</h3>
				<p>
					Order window opens Tuesday. Customers browse, add to cart, check out. Simple as any online
					store.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">3</div>
				<h3>Farmers Harvest to Order</h3>
				<p>Friday: print your pick list. Harvest exactly what's sold. No waste, no guessing.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">4</div>
				<h3>Easy Pickup Day</h3>
				<p>
					Saturday: everything's labeled and sorted. Customers grab their orders, chat a bit, head
					home happy.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- Testimonials -->
<!-- Commented out until we have real testimonials
<section class="testimonials">
	<div class="container">
		<h2 class="mb-4 text-center">Real Markets, Real Impact</h2>
		<div class="testimonial-card">
			<p class="testimonial-text">
				"LocallyGrown.net didn't just grow our market—it transformed our community. We went from 20
				customers to over 200 families eating local food every week. It's changed lives, including
				mine."
			</p>
			<div class="testimonial-author">Sarah Johnson</div>
			<div class="testimonial-role">Manager, Athens Locally Grown</div>
		</div>
		<div class="testimonial-card">
			<p class="testimonial-text">
				"I wake up knowing exactly what to harvest. No waste, no stress, just grateful customers who
				can't wait for pickup day. This platform gave me back my mornings."
			</p>
			<div class="testimonial-author">Mike Chen</div>
			<div class="testimonial-role">Grower, Cumberland Farmers Market</div>
		</div>
	</div>
</section>
-->

<!-- CTA Section -->
<section class="cta-section">
	<div class="container">
		<h2>Ready to Get Started?</h2>
		<p>Free to try. No credit card needed. Get your market online in about 20 minutes.</p>
		<a href="/markets/new" class="btn btn-primary">Create Your Market Today</a>
	</div>
</section>

<style>
	/* Layout utilities - these supplement the marketing design system */
	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.text-center {
		text-align: center;
	}

	.mb-4 {
		margin-bottom: 2rem;
	}

	.mt-4 {
		margin-top: 2rem;
	}

	/* Hero Section */
	.hero {
		padding: 3rem 0;
		background: linear-gradient(
			135deg,
			var(--marketing-bg-white) 0%,
			var(--marketing-bg-tertiary) 100%
		);
		text-align: center;
	}

	.hero h1 {
		font-size: 2.5rem;
		margin-bottom: 1.25rem;
		color: var(--marketing-primary);
		font-weight: 700;
		line-height: 1.2;
	}

	.hero p {
		font-size: 1.125rem;
		max-width: 650px;
		margin: 0 auto 2rem;
		color: var(--marketing-text-muted);
		line-height: 1.6;
	}

	.hero-buttons {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 2rem;
	}

	.hero-buttons .btn {
		padding: 0.625rem 1.25rem;
		border-radius: 6px;
		text-decoration: none;
		font-weight: 500;
		transition: all 0.2s;
		font-size: 0.95rem;
	}

	.hero-buttons .btn-primary {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		border: 2px solid var(--marketing-primary);
	}

	.hero-buttons .btn-primary:hover,
	.hero-buttons .btn-primary:focus,
	.hero-buttons .btn-primary:active {
		background: var(--marketing-primary-hover);
		border-color: var(--marketing-primary-hover);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	.hero-buttons .btn-secondary {
		background: var(--marketing-bg-white);
		color: var(--marketing-primary);
		border: 2px solid var(--marketing-primary);
	}

	.hero-buttons .btn-secondary:hover,
	.hero-buttons .btn-secondary:focus,
	.hero-buttons .btn-secondary:active {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	/* Stats Section */
	.stats-section {
		padding: 3rem 0;
		background: var(--marketing-bg-secondary);
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 2rem;
		text-align: center;
	}

	.stat-item {
		padding: 1.5rem;
	}

	.stat-number {
		font-size: 3rem;
		font-weight: 700;
		color: var(--marketing-primary);
		margin-bottom: 0.5rem;
	}

	.stat-description {
		font-size: 1.1rem;
		color: var(--marketing-text-secondary);
		font-weight: 500;
	}

	/* Markets Section */
	.markets-section {
		padding: 4rem 0;
	}

	.map-container {
		height: 400px;
		border-radius: 8px;
		overflow: hidden;
		box-shadow: var(--marketing-shadow-md);
		margin: 2rem 0;
	}

	/* CTA Section */
	.cta-section {
		padding: 4rem 0;
		background: var(--marketing-gradient-primary);
		color: var(--marketing-text-white);
		text-align: center;
	}

	.cta-section h2 {
		color: var(--marketing-text-white) !important;
		margin-bottom: 1rem;
		font-size: 2.5rem;
	}

	.cta-section p {
		color: var(--marketing-text-white) !important;
		opacity: 0.95;
		max-width: 600px;
		margin: 0 auto 2rem;
		font-size: 1.1rem;
	}

	.cta-section .btn {
		background: var(--marketing-bg-white);
		color: var(--marketing-primary) !important;
		padding: 0.75rem 2rem;
		font-weight: 600;
	}

	.cta-section .btn:hover,
	.cta-section .btn:focus,
	.cta-section .btn:active {
		background: var(--marketing-bg-tertiary);
		transform: translateY(-2px);
	}

	/* Map Placeholder Styles */
	.map-placeholder {
		height: 400px;
		border-radius: 8px;
		background: linear-gradient(
			135deg,
			var(--marketing-bg-tertiary) 0%,
			var(--marketing-bg-secondary) 100%
		);
		border: 2px dashed var(--marketing-border-light);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s ease;
		margin: 2rem 0;
	}

	.map-placeholder:hover,
	.map-placeholder:focus,
	.map-placeholder:active {
		border-color: var(--marketing-primary);
		background: linear-gradient(
			135deg,
			var(--marketing-bg-secondary) 0%,
			var(--marketing-bg-tertiary) 100%
		);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	.placeholder-content {
		text-align: center;
		padding: 2rem;
	}

	.placeholder-icon {
		font-size: 3rem;
		margin-bottom: 1rem;
	}

	.placeholder-content h3 {
		color: var(--marketing-primary);
		margin-bottom: 0.5rem;
		font-size: 1.5rem;
	}

	.placeholder-content p {
		color: var(--marketing-text-muted);
		margin-bottom: 1.5rem;
	}

	.load-map-btn {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		border: none;
		padding: 0.75rem 1.5rem;
		border-radius: 6px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.load-map-btn:hover,
	.load-map-btn:focus,
	.load-map-btn:active {
		background: var(--marketing-primary-hover);
		transform: translateY(-1px);
	}

	.loading-spinner {
		width: 32px;
		height: 32px;
		border: 3px solid var(--marketing-border-light);
		border-top: 3px solid var(--marketing-primary);
		border-radius: 50%;
		animation: spin 1s linear infinite;
		margin: 0 auto;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	@media (max-width: 768px) {
		.hero {
			padding: 2rem 0;
		}

		.hero h1 {
			font-size: 1.75rem;
			line-height: 1.3;
		}

		.hero p {
			font-size: 1rem;
			padding: 0 1rem;
		}

		.hero-buttons {
			flex-direction: column;
			align-items: center;
		}

		.stats-grid {
			grid-template-columns: 1fr;
		}

		.stat-number {
			font-size: 2.5rem;
		}

		.placeholder-content {
			padding: 1rem;
		}

		.placeholder-content h3 {
			font-size: 1.25rem;
		}
	}

	/* Feature Showcase Card */
	.showcase-feature {
		background: var(--marketing-bg-secondary);
		border: 2px solid var(--marketing-border-light);
		position: relative;
		overflow: hidden;
	}

	.showcase-feature::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: var(--marketing-gradient-primary);
	}

	.feature-link {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		margin-top: 1rem;
		color: var(--marketing-primary);
		text-decoration: none;
		font-weight: 600;
		font-size: 0.9rem;
		transition: all 0.3s ease;
	}

	.feature-link:hover,
	.feature-link:focus,
	.feature-link:active {
		color: var(--marketing-primary-hover);
		transform: translateX(4px);
	}

	.feature-link:focus-visible {
		outline: 2px solid var(--marketing-primary);
		outline-offset: 2px;
	}
</style>
